import React, { useEffect, useRef, useState } from "react";
import { Button, ConfirmationModal, MessageBanner, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { usePermissions } from "../hooks/usePermissions";
import { useGetCurrentUser } from "../utils/useCurrentUser";
import { usePendingDeclarationPrefixes } from "../hooks/usePendingDeclarationPrefixes";
import { PendingDeclarationPrompt } from "./PendingDeclarationPrompt";
import styles from "./PublisherDeclarationGate.module.css";
/**
 * Soft entry gate for the Agency module, analogous to the publisher data-confirmation prompt but
 * non-blocking: when the signed-in guarantor/admin's publisher has one or more active prefixes still
 * awaiting a last-used-number declaration, it surfaces a prominent, dismissible banner plus a one-time
 * modal prompt that leads straight to declaring — right here, without leaving for the buried "Prefixy
 * nakladatele" list. It never blocks the routes (unlike the hard {@link PublisherDataRequired} gate)
 * and is only shown to users allowed to declare. It clears itself once no prefix is pending.
 */
export var PublisherDeclarationGate = function () {
    var _a;
    var formatMessage = useIntl().formatMessage;
    var canDeclareLastUsedNumber = usePermissions().canDeclareLastUsedNumber;
    var currentUser = useGetCurrentUser().data;
    var publisherSourceId = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _a === void 0 ? void 0 : _a.sourceId;
    var pendingPrefixes = usePendingDeclarationPrefixes({
        publisherSourceId: publisherSourceId,
        enabled: canDeclareLastUsedNumber,
    }).pendingPrefixes;
    var hasPending = canDeclareLastUsedNumber && pendingPrefixes.length > 0;
    var _b = useState(false), dismissed = _b[0], setDismissed = _b[1];
    var _c = useState(false), promptOpen = _c[0], setPromptOpen = _c[1];
    var _d = useState(false), showDeclare = _d[0], setShowDeclare = _d[1];
    var promptedRef = useRef(false);
    // Open the modal prompt once, the first time a pending prefix is detected this session.
    useEffect(function () {
        if (hasPending && !promptedRef.current) {
            promptedRef.current = true;
            setPromptOpen(true);
        }
    }, [hasPending]);
    if (!hasPending || dismissed) {
        return null;
    }
    return (React.createElement("div", { className: styles.gate },
        React.createElement(MessageBanner, { type: "warning", dismissible: true, onExited: function () { return setDismissed(true); } },
            React.createElement(FormattedMessage, { id: "ui-br-agency.publisher-prefix.declare.gate.banner", values: { count: pendingPrefixes.length } }),
            showDeclare ? (React.createElement(PendingDeclarationPrompt, { publisherSourceId: publisherSourceId, onDeclared: function () { return undefined; } })) : (React.createElement("div", { className: styles.actions },
                React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return setShowDeclare(true); } },
                    React.createElement(FormattedMessage, { id: "ui-br-agency.title.products.panel.assign.reason.declarationPending.action" }))))),
        React.createElement(ConfirmationModal, { open: promptOpen, heading: formatMessage({
                id: "ui-br-agency.publisher-prefix.declare.gate.modal.heading",
            }), message: formatMessage({ id: "ui-br-agency.publisher-prefix.declare.gate.modal.message" }, { count: pendingPrefixes.length }), confirmLabel: formatMessage({
                id: "ui-br-agency.title.products.panel.assign.reason.declarationPending.action",
            }), cancelLabel: formatMessage({
                id: "ui-br-agency.publisher-prefix.declare.gate.modal.later",
            }), onConfirm: function () {
                setPromptOpen(false);
                setShowDeclare(true);
            }, onCancel: function () { return setPromptOpen(false); }, buttonStyle: "primary" })));
};
